import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getV1MessagingChannels, MessagingChannelType } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { useMemo } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import {
  isMessagingChannelTargetReference,
  MessagingChannel,
} from '~hooks/useMessaging/models';
import {
  messagingOrderJobChannelsQueryKey,
  messagingTargetChannelQueryKey,
} from '~queries/messagingQueryKeys';
import type { MessagingChannelsPage } from '~queries/useMessagingChannelsInfiniteQuery';
import { extractPagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';

/**
 * The api returns the order's job channels newest activity first, so the rows keep its order.
 * Membership-scoped: a dispatcher sees only the job conversations they are in.
 */
export default function useMessagingOrderJobChannelsInfiniteQuery({
  orderId,
}: {
  orderId: string;
}) {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: messagingOrderJobChannelsQueryKey(orderId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }): Promise<MessagingChannelsPage> => {
      try {
        const response = await getV1MessagingChannels({
          query: {
            'filter[channel_types]': [MessagingChannelType.JOB],
            'filter[job][order_id]': orderId,
            'page[limit]': DEFAULT_API_PAGE_SIZE,
            ...(pageParam ? { 'page[after]': pageParam } : {}),
          },
          signal,
        });

        const channels = response.data.data.map(MessagingChannel.parse);

        // The job thread looks its channel up by job; each row already is that channel.
        channels.forEach((channel) => {
          if (!channel.target || !isMessagingChannelTargetReference(channel.target))
            return;

          queryClient.setQueryData<MessagingChannel | null>(
            messagingTargetChannelQueryKey(MessagingChannelType.JOB, channel.target.id),
            (cachedChannel) => cachedChannel ?? channel,
          );
        });

        return {
          channels,
          nextCursor: extractPagination(response).after,
        };
      } catch (error) {
        // React Query drops a read it cancelled, so there is nothing to report.
        if (axios.isCancel(error)) throw error;

        reportRequestError(error, t('error_messages.messaging.failed_to_fetch_channels'));

        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // Each attempt reports its own failure, and a failed read already degrades to an empty list.
    retry: false,
  });

  const channels = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.channels),
    [query.data?.pages],
  );

  return {
    channels,
    fetchMore: query.fetchNextPage,
    hasMore: query.hasNextPage,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
  };
}
